import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'src/services/auth.service';
import { LocalSessionService } from 'src/services/local-session.service';
import { HttpClient } from '@angular/common/http';
import firebase from 'firebase';
import { RouteHelperService } from 'src/services/route-helper.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-public-verify-otp',
  templateUrl: './verify-otp.component.html',
  styleUrls: ['./verify-otp.component.css']
})
export class PublicVerifyOtpComponent implements OnInit {

  otpCode: string = '';
  verify: any;
  phoneNumber: any;
  isLoading: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  registrationData: any = null;
  private vehicleCheckCancelled: boolean = false;

  constructor(
    private authService: AuthService,
    private localSessionService: LocalSessionService,
    private http: HttpClient,
    private router: Router,
    public routeHelper: RouteHelperService
  ) { }

  ngOnInit(): void {
    this.verify = JSON.parse(localStorage.getItem('verificationId') || '{}');
    this.phoneNumber = JSON.parse(localStorage.getItem('phoneNumber') || '{}');
    this.registrationData = JSON.parse(localStorage.getItem('registrationData') || '{}');

    if (!this.verify || !this.phoneNumber) {
      this.router.navigate([this.routeHelper.getRoutes().auth]);
    }
  }

  onOtpChange(otpCode: any) {
    this.otpCode = otpCode;
    if (this.otpCode.length === 6) {
      this.verifyOtpCode();
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && this.otpCode.length === 6) {
      this.verifyOtpCode();
    }
  }

  async verifyOtpCode() {
    if (!this.otpCode || this.otpCode.length !== 6) {
      this.errorMessage = "Please enter the 6-digit OTP code to continue.";
      return;
    }

    if (this.otpCode && this.phoneNumber && this.verify) {
      this.isLoading = true;

      try {
        const res = await this.authService.phoneAuthVerification(this.verify, this.otpCode, this.phoneNumber);

        if (res && res.code === 'auth/invalid-verification-code') {
          this.isLoading = false;
          this.errorMessage = "Invalid OTP code. Please try again.";
          return;
        }

        if (res && res.uid) {
          // User is now authenticated with Firebase Auth, so Firestore should work
          console.log('User authenticated:', res.uid);
          
          // Check if this is a new registration or existing user login  
          let userProfile = null;
          try {
            const userDoc = await firebase.firestore().collection('users').doc(res.uid).get();
            
            if (!userDoc.exists && this.registrationData) {
              // This is a new registration, create user profile
              userProfile = await this.createUserProfile(res.uid, this.registrationData);
            } else if (userDoc.exists) {
              userProfile = userDoc.data();
            } else {
              // User exists in Firebase Auth but not in our users collection
              // This happens for existing Firebase users
              userProfile = {
                uid: res.uid,
                fullName: res.displayName || this.phoneNumber,
                email: res.email || null,
                phoneNumber: this.phoneNumber, // Ensure phone number is always set
                dateCreated: new Date(),
                isActive: true
              };
              
              // Create profile for existing Firebase users
              await firebase.firestore().collection('users').doc(res.uid).set(userProfile);
            }
          } catch (firestoreError) {
            console.error('Firestore access error:', firestoreError);
            // Continue with minimal profile if Firestore fails
            userProfile = {
              uid: res.uid,
              fullName: res.displayName || this.phoneNumber,
              email: res.email || null,
              phoneNumber: this.phoneNumber, // Ensure phone number is always set
              dateCreated: new Date(),
              isActive: true
            };
            
          }
          
          // Create a local session (no Firestore required)
          const session = this.localSessionService.createSession(
            this.phoneNumber,
            res.uid,
            userProfile?.fullName || res.displayName || this.phoneNumber,
            userProfile?.email || res.email
          );
          
          // Store in old format for dashboard compatibility
          localStorage.setItem('user', JSON.stringify({
            uid: session.uid,
            phoneNumber: session.phoneNumber,
            displayName: session.displayName,
            email: session.email
          }));
          
          // Clean up temporary data
          localStorage.removeItem('verificationId');
          localStorage.removeItem('phoneNumber');
          localStorage.removeItem('registrationData');
          
          // Check for vehicles and redirect accordingly
          this.vehicleCheckCancelled = false;
          await this.checkVehiclesAndRedirect(session.uid);
        } else {
          this.errorMessage = "Verification failed. Please try again.";
        }
      } catch (error) {
        console.error('OTP verification error:', error);
        this.errorMessage = "Verification failed. Please try again.";
      } finally {
        this.isLoading = false;
      }
    }
  }

  async createUserProfile(uid: string, registrationData: any) {
    try {
      const userProfile = {
        uid: uid,
        email: registrationData?.email || null,
        fullName: registrationData?.fullName || '',
        displayName: registrationData?.fullName || '',
        photoURL: null,
        emailVerified: false,
        phoneNumber: this.phoneNumber || '',
        phoneNumberVerified: true,
        timestamp: new Date(),
        roles: {
          standard: true,
          subscriber: true
        }
      };
      
      await firebase.firestore().collection('users').doc(uid).set(userProfile, { merge: true });
      return userProfile;
    } catch (error) {
      console.error('Error creating user profile:', error);
      return null;
    }
  }


  resendOTP() {
    // Clear error message
    this.errorMessage = '';
    
    // Navigate back to auth to resend OTP
    localStorage.removeItem('verificationId');
    this.router.navigate([this.routeHelper.getRoutes().auth]);
  }

  generateRandomString(length: number): string {
    let result = '';
    let characters = '0123456789';
    let charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  async checkVehiclesAndRedirect(userId: string) {
    try {
      if (this.vehicleCheckCancelled || localStorage.getItem('cancelVehicleCheck') === 'true') {
        localStorage.removeItem('cancelVehicleCheck');
        return;
      }
      
      this.successMessage = 'Loading your vehicles...';
      
      const response = await this.http.post(`${environment.apiBaseUrl}/getUserVehicles`, {
        owner_id: userId
      }).toPromise();
      
      if (this.vehicleCheckCancelled || localStorage.getItem('cancelVehicleCheck') === 'true') {
        localStorage.removeItem('cancelVehicleCheck');
        return;
      }
      
      const userVehicles = response && Array.isArray(response) ? response : [];
      
      if (userVehicles.length > 0) {
        this.successMessage = 'Redirecting to dashboard...';
        setTimeout(() => {
          if (!this.vehicleCheckCancelled && localStorage.getItem('cancelVehicleCheck') !== 'true') {
            this.router.navigate([this.routeHelper.getRoutes().dashboard]);
          } else {
            localStorage.removeItem('cancelVehicleCheck');
          }
        }, 1000);
      } else {
        this.successMessage = 'Setting up your vehicle registration...';
        setTimeout(() => {
          if (!this.vehicleCheckCancelled && localStorage.getItem('cancelVehicleCheck') !== 'true') {
            this.router.navigate([this.routeHelper.getRoutes().vehicleRegistration]);
          } else {
            localStorage.removeItem('cancelVehicleCheck');
          }
        }, 1000);
      }
    } catch (error) {
      if (this.vehicleCheckCancelled || localStorage.getItem('cancelVehicleCheck') === 'true') {
        localStorage.removeItem('cancelVehicleCheck');
        return;
      }
      
      console.error('Error checking user vehicles:', error);
      this.successMessage = 'Redirecting to dashboard...';
      setTimeout(() => {
        if (!this.vehicleCheckCancelled && localStorage.getItem('cancelVehicleCheck') !== 'true') {
          this.router.navigate([this.routeHelper.getRoutes().dashboard]);
        } else {
          localStorage.removeItem('cancelVehicleCheck');
        }
      }, 1000);
    }
  }

  cancelVehicleCheck() {
    this.vehicleCheckCancelled = true;
    this.successMessage = '';
  }
}